// 保單名稱優化服務
export interface PolicyNameOptimizationResult {
  originalName: string;
  companyName: string;
  optimizedName: string;
  removedParts: string[];
  confidence: number;
}

class PolicyNameOptimizer {
  
  // 主要優化方法
  optimizePolicyName(policyName: string, companyName: string): PolicyNameOptimizationResult {
    if (!policyName || !companyName) {
      return {
        originalName: policyName,
        companyName: companyName,
        optimizedName: policyName,
        removedParts: [],
        confidence: 0
      };
    }

    console.log('開始優化保單名稱:', { policyName, companyName });

    // 清理和標準化名稱
    const cleanPolicyName = this.cleanText(policyName);
    const cleanCompanyName = this.cleanText(companyName);

    // 方法1: 直接匹配移除
    let optimizedName = this.removeDirectMatches(cleanPolicyName, cleanCompanyName);
    let removedParts = this.getRemovedParts(cleanPolicyName, optimizedName);

    // 方法2: 智能分割移除
    if (removedParts.length === 0) {
      const smartResult = this.smartRemoveCompanyName(cleanPolicyName, cleanCompanyName);
      optimizedName = smartResult.optimizedName;
      removedParts = smartResult.removedParts;
    }

    // 方法3: 關鍵字模式匹配
    if (removedParts.length === 0) {
      const patternResult = this.removeByPatterns(cleanPolicyName, cleanCompanyName);
      optimizedName = patternResult.optimizedName;
      removedParts = patternResult.removedParts;
    }

    // 計算優化可信度
    const confidence = this.calculateOptimizationConfidence(
      cleanPolicyName, 
      optimizedName, 
      removedParts.length
    );

    // 如果優化後名稱太短，保持原名
    if (optimizedName.length < 3) {
      optimizedName = policyName;
      removedParts = [];
    }

    const result = {
      originalName: policyName,
      companyName: companyName,
      optimizedName: optimizedName,
      removedParts: removedParts,
      confidence: confidence
    };

    console.log('保單名稱優化結果:', result);
    return result;
  }

  // 清理文本
  private cleanText(text: string): string {
    return text
      .replace(/[（）()【】[\]「」""''《》]/g, '') // 移除括號
      .replace(/\s+/g, '') // 移除空格
      .trim();
  }

  // 直接匹配移除
  private removeDirectMatches(policyName: string, companyName: string): string {
    let result = policyName;

    // 完全匹配
    if (result.includes(companyName)) {
      result = result.replace(companyName, '');
    }

    // 部分匹配（公司名稱的80%以上）
    const minMatchLength = Math.floor(companyName.length * 0.8);
    for (let i = 0; i <= companyName.length - minMatchLength; i++) {
      const substring = companyName.substring(i, i + minMatchLength);
      if (result.includes(substring)) {
        result = result.replace(substring, '');
        break;
      }
    }

    return result.trim();
  }

  // 智能分割移除
  private smartRemoveCompanyName(policyName: string, companyName: string): {
    optimizedName: string;
    removedParts: string[];
  } {
    const removedParts: string[] = [];
    let result = policyName;

    // 分割公司名稱為詞組
    const companyWords = this.splitCompanyName(companyName);
    
    for (const word of companyWords) {
      if (word.length >= 2 && result.includes(word)) {
        result = result.replace(word, '');
        removedParts.push(word);
      }
    }

    return {
      optimizedName: result.trim(),
      removedParts: removedParts
    };
  }

  // 分割公司名稱
  private splitCompanyName(companyName: string): string[] {
    const words: string[] = [];
    
    // 常見保險公司關鍵字
    const insuranceKeywords = [
      '保險', '產物', '人壽', '壽險', '產險', '保險公司', '保險股份有限公司',
      '有限公司', '股份有限公司', '集團', '控股', '金融', '投資', '信託'
    ];

    // 移除常見後綴
    let cleanName = companyName;
    for (const keyword of insuranceKeywords) {
      if (cleanName.endsWith(keyword)) {
        cleanName = cleanName.replace(new RegExp(keyword + '$'), '');
        break;
      }
    }

    // 按字符分割（中文）
    for (let i = 0; i < cleanName.length; i++) {
      const char = cleanName[i];
      if (this.isChinese(char)) {
        words.push(char);
      }
    }

    // 添加2-3字符的組合
    for (let i = 0; i < cleanName.length - 1; i++) {
      const twoChar = cleanName.substring(i, i + 2);
      if (this.isChinese(twoChar)) {
        words.push(twoChar);
      }
    }

    for (let i = 0; i < cleanName.length - 2; i++) {
      const threeChar = cleanName.substring(i, i + 3);
      if (this.isChinese(threeChar)) {
        words.push(threeChar);
      }
    }

    return Array.from(new Set(words)); // 去重
  }

  // 模式匹配移除
  private removeByPatterns(policyName: string, companyName: string): {
    optimizedName: string;
    removedParts: string[];
  } {
    const removedParts: string[] = [];
    let result = policyName;

    // 常見模式
    const patterns = [
      // 公司名稱 + 保險類型
      new RegExp(`^${this.escapeRegex(companyName)}[保險產物人壽壽險產險].*`, 'g'),
      // 保險類型 + 公司名稱
      new RegExp(`.*[保險產物人壽壽險產險]${this.escapeRegex(companyName)}$`, 'g'),
      // 公司名稱開頭
      new RegExp(`^${this.escapeRegex(companyName)}`, 'g'),
      // 公司名稱結尾
      new RegExp(`${this.escapeRegex(companyName)}$`, 'g')
    ];

    for (const pattern of patterns) {
      const match = result.match(pattern);
      if (match) {
        result = result.replace(pattern, '');
        removedParts.push(...match);
        break;
      }
    }

    return {
      optimizedName: result.trim(),
      removedParts: removedParts
    };
  }

  // 獲取被移除的部分
  private getRemovedParts(original: string, optimized: string): string[] {
    if (original === optimized) return [];
    
    const removed = original.replace(optimized, '').trim();
    return removed ? [removed] : [];
  }

  // 計算優化可信度
  private calculateOptimizationConfidence(
    originalName: string, 
    optimizedName: string, 
    removedCount: number
  ): number {
    if (removedCount === 0) return 0;
    
    const reductionRatio = (originalName.length - optimizedName.length) / originalName.length;
    const baseConfidence = Math.min(reductionRatio * 2, 0.9); // 最多90%
    
    // 如果移除後名稱仍然合理（長度適中），提高可信度
    if (optimizedName.length >= 3 && optimizedName.length <= originalName.length * 0.7) {
      return Math.min(baseConfidence + 0.1, 0.95);
    }
    
    return baseConfidence;
  }

  // 檢查是否為中文字符
  private isChinese(char: string): boolean {
    return /[\u4e00-\u9fff]/.test(char);
  }

  // 轉義正則表達式特殊字符
  private escapeRegex(string: string): string {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

export const policyNameOptimizer = new PolicyNameOptimizer();
